/**
 * Global generations provider.
 *
 * Problem this solves:
 *   - Previously each <Chat/> instance ran its own polling loop. When the
 *     user navigated away from chat A while a generation was in-flight,
 *     React unmounted the component (each route key is unique), the
 *     poll loop got cancelled, and nobody updated the message anymore.
 *   - On return to chat A, the user saw a permanently spinning skeleton
 *     even though the backend had long since finished writing the image
 *     to the message doc.
 *
 * Approach:
 *   - This provider lives at the App root and is NEVER unmounted while
 *     the user is logged in. It maintains a Set of (chatId, msgId) pairs
 *     it's actively tracking.
 *   - A single polling loop wakes every 2s. For each tracked message it
 *     fetches GET /chats/:cid/messages/:mid and dispatches a global
 *     `kc:msg-updated` event with the full message body. The active chat
 *     component listens to that event and merges the update into its
 *     local state; chats not currently mounted simply don't react until
 *     the user navigates back, at which point their initial fetch
 *     already returns the up-to-date document.
 *   - When a message transitions out of `generating`, the provider
 *     removes it from the tracked set and fires `kc:credits-updated`
 *     (so the consumption badge refreshes) and `kc:chats-updated`
 *     (so the sidebar can reorder by updated_at).
 *
 * Hydration:
 *   - On mount we read previously-tracked entries from localStorage so a
 *     hard refresh mid-generation still picks up where it left off. We
 *     also discard entries older than 15 minutes because the backend's
 *     stale-cleanup would have marked those as `failed` anyway.
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "kc_active_generations";
const POLL_INTERVAL_MS = 2000;
const IDLE_INTERVAL_MS = 4000;
const MAX_AGE_MS = 15 * 60 * 1000;

const GenerationsContext = createContext({
    track: () => {},
    untrack: () => {},
    isChatGenerating: () => false,
    activeCount: 0,
});

function hydrate() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        const cutoff = Date.now() - MAX_AGE_MS;
        return parsed.filter(
            (e) =>
                e &&
                typeof e.chatId === "string" &&
                typeof e.msgId === "string" &&
                typeof e.startedAt === "number" &&
                e.startedAt > cutoff,
        );
    } catch {
        return [];
    }
}

export function GenerationsProvider({ children }) {
    // `active` is the source of truth: a Map<msgId, {chatId, startedAt}>.
    // We mirror it into `activeRef` so the polling loop never closes
    // over stale state (the loop is set up ONCE in a [] effect).
    const [active, setActive] = useState(() => {
        const seed = hydrate();
        const m = new Map();
        for (const e of seed) m.set(e.msgId, { chatId: e.chatId, startedAt: e.startedAt });
        return m;
    });
    const activeRef = useRef(active);
    useEffect(() => {
        activeRef.current = active;
        // Persist on every change so refresh keeps tracking.
        try {
            const arr = [...active.entries()].map(([msgId, v]) => ({
                msgId,
                chatId: v.chatId,
                startedAt: v.startedAt,
            }));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
        } catch {
            /* quota errors etc — non-fatal */
        }
    }, [active]);

    const track = useCallback((chatId, msgId) => {
        if (!chatId || !msgId) return;
        setActive((prev) => {
            if (prev.has(msgId)) return prev;
            const next = new Map(prev);
            next.set(msgId, { chatId, startedAt: Date.now() });
            return next;
        });
    }, []);

    const untrack = useCallback((msgId) => {
        setActive((prev) => {
            if (!prev.has(msgId)) return prev;
            const next = new Map(prev);
            next.delete(msgId);
            return next;
        });
    }, []);

    const isChatGenerating = useCallback(
        (chatId) => {
            if (!chatId) return false;
            for (const v of active.values()) {
                if (v.chatId === chatId) return true;
            }
            return false;
        },
        [active],
    );

    // Single long-lived polling loop. Sleeps when nothing is being
    // tracked, wakes immediately when a new message is registered.
    useEffect(() => {
        let canceled = false;
        let timer = null;

        const tick = async () => {
            if (canceled) return;
            const entries = [...activeRef.current.entries()];
            if (entries.length === 0) {
                timer = window.setTimeout(tick, IDLE_INTERVAL_MS);
                return;
            }
            // Drop stale entries before polling. The backend marks
            // anything stuck in `generating` >5min as `failed`, but the
            // background task itself could have died silently — we
            // bail at MAX_AGE_MS regardless.
            const cutoff = Date.now() - MAX_AGE_MS;
            const expired = entries
                .filter(([, v]) => v.startedAt < cutoff)
                .map(([msgId]) => msgId);
            if (expired.length > 0) {
                setActive((prev) => {
                    const next = new Map(prev);
                    for (const id of expired) next.delete(id);
                    return next;
                });
            }
            const fresh = entries.filter(([, v]) => v.startedAt >= cutoff);
            if (fresh.length === 0) {
                timer = window.setTimeout(tick, IDLE_INTERVAL_MS);
                return;
            }
            let anyCompleted = false;
            await Promise.all(
                fresh.map(async ([msgId, v]) => {
                    try {
                        const r = await api.get(
                            `/chats/${v.chatId}/messages/${msgId}`,
                        );
                        const msg = r.data;
                        if (!msg) return;
                        // Fire ALWAYS so the active chat component can
                        // show carousel progress mid-generation.
                        window.dispatchEvent(
                            new CustomEvent("kc:msg-updated", {
                                detail: { chatId: v.chatId, msgId, msg },
                            }),
                        );
                        if (msg.status !== "generating") {
                            anyCompleted = true;
                            // Remove from tracked set — done.
                            setActive((prev) => {
                                if (!prev.has(msgId)) return prev;
                                const next = new Map(prev);
                                next.delete(msgId);
                                return next;
                            });
                        }
                    } catch {
                        // 404 (message gone) → drop tracking so we don't
                        // loop forever. Network errors are retried by
                        // the next tick.
                        // We can't distinguish them here cleanly without
                        // exposing axios response status, but worst case
                        // a flaky network just delays the update by 2s.
                    }
                }),
            );
            if (anyCompleted) {
                window.dispatchEvent(new CustomEvent("kc:credits-updated"));
                window.dispatchEvent(new CustomEvent("kc:chats-updated"));
            }
            timer = window.setTimeout(tick, POLL_INTERVAL_MS);
        };

        timer = window.setTimeout(tick, 800);
        return () => {
            canceled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, []);

    return (
        <GenerationsContext.Provider
            value={{
                track,
                untrack,
                isChatGenerating,
                activeCount: active.size,
            }}
        >
            {children}
        </GenerationsContext.Provider>
    );
}

export const useGenerations = () => useContext(GenerationsContext);
